export const CONTINUITY_CODE_LENGTH = 8;
export const MAX_CONTINUITY_CARDS = 10_000;
export const CONTINUITY_GENERATION_ATTEMPTS = 8;
export const CONTINUITY_ALGORITHM = "hmac-sha256-crockford8-v1";

const CONTINUITY_DOMAIN = "convoful.private-continuity.v1";
const CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const NONCE_BYTE_LENGTH = 32;
const continuityKeys = new WeakMap<
  ContinuityManifest,
  Promise<CryptoKey>
>();
const MANIFEST_KEYS = [
  "schemaVersion",
  "kind",
  "algorithm",
  "principalUserID",
  "audienceID",
  "sessionID",
  "generationID",
  "nonce",
  "orderedCardIDs",
  "createdAt",
] as const;

export interface ContinuityManifest {
  schemaVersion: 1;
  kind: "private_continuity_manifest";
  algorithm: typeof CONTINUITY_ALGORITHM;
  principalUserID: string;
  audienceID: string;
  sessionID: string;
  generationID: string;
  nonce: string;
  orderedCardIDs: string[];
  createdAt: string;
}

export interface ContinuityIdentity {
  principalUserID: string;
  audienceID: string;
  sessionID: string;
}

export interface ContinuityManifestInput extends ContinuityIdentity {
  orderedCardIDs: readonly string[];
}

export interface ResolvedContinuityManifest {
  manifest: ContinuityManifest;
  codesByCardID: ReadonlyMap<string, string>;
}

export interface ContinuityDependencies {
  randomBytes?: (length: number) => Uint8Array;
  createID?: () => string;
  now?: () => string;
  deriveCode?: (
    manifest: ContinuityManifest,
    cardID: string,
    position: number,
  ) => Promise<string>;
  maxAttempts?: number;
}

export async function generateContinuityManifest(
  input: ContinuityManifestInput,
  dependencies: ContinuityDependencies = {},
): Promise<ResolvedContinuityManifest> {
  assertIdentity(input);
  assertOrderedCardIDs(input.orderedCardIDs);
  const randomBytes = dependencies.randomBytes ?? secureRandomBytes;
  const createID = dependencies.createID ?? (() => crypto.randomUUID());
  const now = dependencies.now ?? (() => new Date().toISOString());
  const deriveCode = dependencies.deriveCode ?? deriveContinuityCode;
  const maxAttempts = dependencies.maxAttempts
    ?? CONTINUITY_GENERATION_ATTEMPTS;
  if (
    !Number.isSafeInteger(maxAttempts)
    || maxAttempts < 1
    || maxAttempts > 32
  ) {
    throw new Error("Continuity generation requires a bounded retry count.");
  }

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const nonceBytes = randomBytes(NONCE_BYTE_LENGTH);
    if (
      !(nonceBytes instanceof Uint8Array)
      || nonceBytes.byteLength !== NONCE_BYTE_LENGTH
    ) {
      throw new Error("Continuity generation requires 32 random bytes.");
    }
    const manifest: ContinuityManifest = {
      schemaVersion: 1,
      kind: "private_continuity_manifest",
      algorithm: CONTINUITY_ALGORITHM,
      principalUserID: input.principalUserID,
      audienceID: input.audienceID,
      sessionID: input.sessionID,
      generationID: createID(),
      nonce: encodeBase64URL(nonceBytes),
      orderedCardIDs: [...input.orderedCardIDs],
      createdAt: now(),
    };
    assertContinuityManifest(manifest);
    const codesByCardID = await deriveManifestCodes(manifest, deriveCode);
    if (new Set(codesByCardID.values()).size === codesByCardID.size) {
      return {
        manifest: structuredClone(manifest),
        codesByCardID,
      };
    }
  }
  throw new Error(
    "A collision-free continuity manifest could not be generated.",
  );
}

export async function resolveContinuityManifest(
  value: unknown,
  identity: ContinuityIdentity,
  dependencies: Pick<ContinuityDependencies, "deriveCode"> = {},
): Promise<ResolvedContinuityManifest> {
  assertIdentity(identity);
  assertContinuityManifest(value);
  if (
    value.principalUserID !== identity.principalUserID
    || value.audienceID !== identity.audienceID
    || value.sessionID !== identity.sessionID
  ) {
    throw new Error(
      "The continuity manifest does not belong to this private session.",
    );
  }
  const codesByCardID = await deriveManifestCodes(
    value,
    dependencies.deriveCode ?? deriveContinuityCode,
  );
  if (new Set(codesByCardID.values()).size !== codesByCardID.size) {
    throw new Error("The continuity manifest contains a code collision.");
  }
  return {
    manifest: structuredClone(value),
    codesByCardID,
  };
}

export function continuityCodeForCard(
  resolved: ResolvedContinuityManifest | null,
  cardID: string,
): string | null {
  if (!resolved || !isBoundedIdentifier(cardID, 512)) return null;
  return resolved.codesByCardID.get(cardID) ?? null;
}

export function assertContinuityManifest(
  value: unknown,
): asserts value is ContinuityManifest {
  if (
    typeof value !== "object"
    || value === null
    || Array.isArray(value)
    || !hasOnlyKeys(value, MANIFEST_KEYS)
  ) {
    throw new Error("Stored continuity manifest has an invalid envelope.");
  }
  const manifest = value as Partial<ContinuityManifest>;
  if (
    manifest.schemaVersion !== 1
    || manifest.kind !== "private_continuity_manifest"
    || manifest.algorithm !== CONTINUITY_ALGORITHM
    || !isBoundedIdentifier(manifest.principalUserID, 256)
    || !isBoundedIdentifier(manifest.audienceID, 256)
    || !isBoundedIdentifier(manifest.sessionID, 256)
    || !isBoundedIdentifier(manifest.generationID, 256)
    || typeof manifest.nonce !== "string"
    || !isValidNonce(manifest.nonce)
    || !Array.isArray(manifest.orderedCardIDs)
    || !isValidTimestamp(manifest.createdAt)
  ) {
    throw new Error("Stored continuity manifest is invalid.");
  }
  assertOrderedCardIDs(manifest.orderedCardIDs);
}

async function deriveManifestCodes(
  manifest: ContinuityManifest,
  deriveCode: NonNullable<ContinuityDependencies["deriveCode"]>,
): Promise<Map<string, string>> {
  const entries = await Promise.all(
    manifest.orderedCardIDs.map(async (cardID, position) => {
      const code = await deriveCode(manifest, cardID, position);
      if (
        typeof code !== "string"
        || code.length !== CONTINUITY_CODE_LENGTH
        || [...code].some((character) => (
          !CROCKFORD_ALPHABET.includes(character)
        ))
      ) {
        throw new Error("Continuity derivation returned an invalid code.");
      }
      return [cardID, code] as const;
    }),
  );
  return new Map(entries);
}

async function deriveContinuityCode(
  manifest: ContinuityManifest,
  cardID: string,
  position: number,
): Promise<string> {
  let keyPromise = continuityKeys.get(manifest);
  if (!keyPromise) {
    const keyBytes = new Uint8Array(decodeBase64URL(manifest.nonce));
    keyPromise = crypto.subtle.importKey(
      "raw",
      keyBytes,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    );
    continuityKeys.set(manifest, keyPromise);
  }
  const key = await keyPromise;
  const input = new TextEncoder().encode(JSON.stringify([
    CONTINUITY_DOMAIN,
    manifest.algorithm,
    manifest.principalUserID,
    manifest.audienceID,
    manifest.sessionID,
    manifest.generationID,
    position,
    cardID,
  ]));
  const signature = new Uint8Array(
    await crypto.subtle.sign("HMAC", key, input),
  );
  return encodeCrockford40(signature.subarray(0, 5));
}

function encodeCrockford40(bytes: Uint8Array): string {
  if (bytes.byteLength !== 5) {
    throw new Error("A continuity code requires exactly 40 derived bits.");
  }
  let value = 0n;
  for (const byte of bytes) value = (value << 8n) | BigInt(byte);
  let result = "";
  for (let shift = 35n; shift >= 0n; shift -= 5n) {
    const index = Number((value >> shift) & 31n);
    result += CROCKFORD_ALPHABET[index];
  }
  return result;
}

function secureRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

function encodeBase64URL(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

function decodeBase64URL(value: string): Uint8Array {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(
    `${value.replaceAll("-", "+").replaceAll("_", "/")}${padding}`,
  );
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function isValidNonce(value: string): boolean {
  if (!/^[A-Za-z0-9_-]{43}$/u.test(value)) return false;
  try {
    const bytes = decodeBase64URL(value);
    return (
      bytes.byteLength === NONCE_BYTE_LENGTH
      && encodeBase64URL(bytes) === value
    );
  } catch {
    return false;
  }
}

function assertIdentity(identity: ContinuityIdentity): void {
  if (
    !isBoundedIdentifier(identity.principalUserID, 256)
    || !isBoundedIdentifier(identity.audienceID, 256)
    || !isBoundedIdentifier(identity.sessionID, 256)
  ) {
    throw new Error(
      "Continuity generation requires a private principal, audience, and session.",
    );
  }
}

function assertOrderedCardIDs(cardIDs: readonly unknown[]): void {
  if (
    cardIDs.length < 1
    || cardIDs.length > MAX_CONTINUITY_CARDS
    || cardIDs.some((cardID) => !isBoundedIdentifier(cardID, 512))
    || new Set(cardIDs).size !== cardIDs.length
  ) {
    throw new Error(
      `A continuity manifest requires 1 to ${MAX_CONTINUITY_CARDS} unique cards.`,
    );
  }
}

function isBoundedIdentifier(
  value: unknown,
  maximumLength: number,
): value is string {
  return (
    typeof value === "string"
    && value === value.trim()
    && value.length > 0
    && value.length <= maximumLength
    && !/[\u0000-\u001f\u007f]/u.test(value)
  );
}

function isValidTimestamp(value: unknown): value is string {
  return (
    typeof value === "string"
    && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u.test(value)
    && !Number.isNaN(new Date(value).valueOf())
  );
}

function hasOnlyKeys(
  value: object,
  keys: readonly string[],
): boolean {
  const allowed = new Set(keys);
  return Object.keys(value).every((key) => allowed.has(key))
    && keys.every((key) => key in value);
}
